import {onMounted, Ref, ref, watch} from '@vue/composition-api'
import {debounce} from 'lodash'

import apiOffers from '@/api/offers.api'
import {mapResponseDataToOffers} from '@/mappers/offers'
import {OpenSearchListRequest, OpenSearchPagination} from '@/types'
import {Status} from '@/types/offers'
import {OFFER_EXPIRE_DAYS} from '@/utils/offers'

interface Props {
  dealerId: string
  dealerCode: string
  search?: string
  pagination: Partial<OpenSearchPagination>
  queryParams?: Record<string, any>
  externalText?: Ref<string>
  externalStatuses?: Ref<Status[]>
  externalRange?: Ref<boolean>
}

export const useOffersList = <T>(props: Props) => {
  const list = ref<Array<T>>([]) as Ref<Array<T>>
  const text = props.externalText ?? ref('')
  const queryParams = ref(props.queryParams || {})
  const isLoading = ref(false)
  const total = ref(0)
  const pagination = ref<Partial<OpenSearchPagination>>(props.pagination)
  const range = props.externalRange ?? ref(false)

  if (props.externalStatuses) {
    queryParams.value = {...queryParams.value, statuses: props.externalStatuses.value}
    watch(
      props.externalStatuses,
      newStatuses => {
        queryParams.value = {...queryParams.value, statuses: newStatuses}
      },
      {deep: true},
    )
  }

  const debounceFetch = debounce(fetchOffers, 500)

  onMounted(async () => {
    await fetchOffers()
  })

  watch([text, range, queryParams], debounceFetch, {deep: true})

  async function fetchOffers() {
    isLoading.value = true

    try {
      const request: OpenSearchListRequest = {
        dealer: {id: props.dealerId, code: props.dealerCode},
        pagination: pagination.value,
        params: queryParams.value,
        text: cleanSearchInput(text.value),
        range: !range.value ? OFFER_EXPIRE_DAYS : undefined,
      }

      const response = await apiOffers.getOffersListByDealerId(request)
      const {value: totalHits} = response.total
      const data = mapResponseDataToOffers(response.hits || response.data)
      const filteredData = (data || []).filter((item: any) => range.value || !item.expired)

      list.value = filteredData
      total.value = Math.max(totalHits, filteredData.length)
    } catch (e) {
      console.error('Error fetching the offer list', e)
    }

    isLoading.value = false
  }

  function cleanSearchInput(input?: string) {
    if (!input) return ''
    return input.trim().replace(/^\+/, '')
  }

  return {
    isLoading,
    data: list,
    text,
    range,
    total,
    queryParams,
    pagination,
    refetch: fetchOffers,
    debounceRefetch: debounceFetch,
  }
}
